import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import "./book.css";
import { Button, Input } from "../lib/ui.js";
import { IntakeFieldInput } from "../lib/IntakeFieldInput.js";
import { emptyCustomFieldValues } from "../lib/intakeFields.js";
import { formatServicePrice } from "../lib/money.js";
import { formatAuPhoneForDisplay } from "../lib/phone.js";
import { formatTime, formatWeekdayDayMonth } from "../lib/datetime.js";
import { setDisplayTimeZone, todayInZone } from "../lib/zone.js";
import { bookingApi, BookingApiError, type BookedVisit, type MyBooking } from "./api.js";
import {
  clearSession, durationLabel, errorMessage, groupByCategory, loadSession, questionsFor, saveSession, SESSION_MS, staffForServices, totalMinutes,
  type PublicConfig, type SavedSession,
} from "./helpers.js";
import { SlotCalendar, type PickedTime } from "./SlotCalendar.js";
import { VerifyStep, type Verified } from "./VerifyStep.js";
import { ManageFlow } from "./ManageFlow.js";
import { DobCheck } from "./DobCheck.js";

/** Tells a page that embeds this one how tall it is, so the frame can fit (see public/embed.js). */
function useReportHeight(enabled: boolean) {
  useEffect(() => {
    if (!enabled || window.parent === window) return;
    const send = () => window.parent.postMessage({ type: "receptionist-booking-height", height: document.documentElement.scrollHeight }, "*");
    const observer = new ResizeObserver(send);
    observer.observe(document.body);
    send();
    return () => observer.disconnect();
  }, [enabled]);
}

/**
 * The public booking page, /book/<business>, laid out like the portal's New booking: a panel (services, who you are, details, confirm)
 * beside a day view with a column for each staff member. Everything is on the page at once; nothing is a step. In portrait, or when the
 * screen is narrow, the panel and the day view stack. A customer checks their mobile with a texted code; verified once, the same
 * browser tab may keep booking for 20 minutes without another code. Cancelling or moving always needs a fresh code (ManageFlow).
 */
export function BookingPage() {
  const { slug = "" } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const api = useMemo(() => bookingApi(slug), [slug]);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mode, setMode] = useState<"book" | "manage">("book");
  useReportHeight(embed);

  // Chrome on Android treats "tap, then quickly drag" as the one-handed zoom gesture, so the first swipe after tapping a time did nothing.
  // `manipulation` turns double-tap zoom off for the whole page (pinch zoom still works).
  useEffect(() => {
    const html = document.documentElement;
    const before = html.style.touchAction;
    html.style.touchAction = "manipulation";
    return () => {
      html.style.touchAction = before;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .config()
      .then((c) => {
        if (cancelled) return;
        setDisplayTimeZone(c.timezone); // every time on this page is the business's own
        document.title = `Book with ${c.name}`;
        setConfig(c);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(errorMessage(e instanceof BookingApiError ? e.code : "unexpected_error"));
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <div className={`bk-page${embed ? " bk-embed" : ""}`}>
      <div className="bk-wrap">
        {loadError && <p className="bk-error" role="alert">{loadError}</p>}
        {!config && !loadError && <p className="bk-muted">Loading...</p>}
        {config && (
          <>
            {!embed && (
              <header>
                <h1 className="bk-title">
                  {config.name} <span className="bk-titlesub">Book online</span>
                </h1>
                {(config.address || config.phone) && (
                  <p className="bk-sub bk-contact">
                    {config.address && <span>{config.address}</span>}
                    {config.phone && <a href={`tel:${config.phone}`}>Ph. {formatAuPhoneForDisplay(config.phone)}</a>}
                  </p>
                )}
              </header>
            )}
            <div className="bk-tabs" role="tablist" aria-label="What would you like to do?">
              <button type="button" role="tab" aria-selected={mode === "book"} className="bk-chip" aria-pressed={mode === "book"} onClick={() => setMode("book")}>
                Make a booking
              </button>
              <button type="button" role="tab" aria-selected={mode === "manage"} className="bk-chip" aria-pressed={mode === "manage"} onClick={() => setMode("manage")}>
                Change or cancel
              </button>
            </div>
            {mode === "book" ? (
              <BookFlow api={api} slug={slug} config={config} onManage={() => setMode("manage")} />
            ) : (
              <ManageFlow api={api} config={config} onBook={() => setMode("book")} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function BookFlow({ api, slug, config, onManage }: { api: ReturnType<typeof bookingApi>; slug: string; config: PublicConfig; onManage: () => void }) {
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [picked, setPicked] = useState<PickedTime | null>(null);
  const [session, setSession] = useState<SavedSession | null>(() => loadSession(slug));
  const [verifyNote, setVerifyNote] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [forSomeone, setForSomeone] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [dob, setDob] = useState("");
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [done, setDone] = useState<BookedVisit | null>(null);
  const [mine, setMine] = useState<MyBooking[]>([]);

  const questions = useMemo(() => questionsFor(config.services, serviceIds), [config.services, serviceIds]);
  const staff = useMemo(() => staffForServices(config.resources, serviceIds), [config.resources, serviceIds]);
  const chosen = config.services.filter((s) => serviceIds.includes(s.id));
  const minutes = totalMinutes(config.services, serviceIds);
  const pickedStaff = picked ? config.resources.find((r) => r.id === picked.resourceId)?.name : null;

  // Once the mobile is checked (and again after each booking made), show the customer's own bookings on the day view.
  const token = session?.token ?? null;
  useEffect(() => {
    if (!token) {
      setMine([]);
      return;
    }
    let cancelled = false;
    api.myCalendar(token).then((list) => !cancelled && setMine(list)).catch(() => !cancelled && setMine([]));
    return () => {
      cancelled = true;
    };
  }, [api, token, done]);

  // The same question keeps its answer while services change; new ones start empty.
  useEffect(() => {
    setAnswers((have) => ({ ...emptyCustomFieldValues(questions), ...Object.fromEntries(Object.entries(have).filter(([k]) => questions.some((q) => q.key === k))) }));
  }, [questions]);

  function toggleService(id: string) {
    setServiceIds((have) => (have.includes(id) ? have.filter((x) => x !== id) : have.length >= 5 ? have : [...have, id]));
    setPicked(null);
    setError(null);
  }

  function onVerified(v: Verified) {
    setSession(saveSession(slug, { token: v.token, phone: v.phone, needsName: v.needsName, name: v.name, dobMode: v.dobMode, dobOk: v.dobMode !== "verify" }));
    setDob("");
    setVerifyNote(null);
    setError(null);
  }

  function endSession(note: string | null) {
    clearSession(slug);
    setSession(null);
    setVerifyNote(note);
  }

  const answered = (q: (typeof questions)[number]) => {
    const v = answers[q.key];
    return q.type === "boolean" ? true : Array.isArray(v) ? v.length > 0 : typeof v === "string" ? v.trim() !== "" : v != null;
  };
  const dobPending = !!session && ((session.dobMode === "verify" && !session.dobOk) || (session.dobMode === "collect" && !dob));
  const ready =
    !!picked && !!session && !dobPending && serviceIds.length > 0 && (!session.needsName || name.trim().length >= 2) && (!forSomeone || guestName.trim().length > 0) &&
    questions.every((q) => !q.required || answered(q));

  /** Enter in a box of the form confirms, like a form would (the code box has a form of its own, so this one is not a <form>). */
  const confirmOnEnter = (e: KeyboardEvent) => {
    const target = e.target as HTMLInputElement;
    if (e.key === "Enter" && target.tagName === "INPUT" && target.type !== "checkbox" && !target.closest("form")) {
      e.preventDefault();
      void submit();
    }
  };

  async function submit() {
    if (!session || !picked || !ready) return;
    setBusy(true);
    setError(null);
    try {
      const visit = await api.book(session.token, {
        serviceIds,
        resourceId: picked.resourceId,
        startsAt: picked.slot.starts_at,
        name: session.needsName ? name.trim() : null,
        guestName: forSomeone ? guestName.trim() || null : null,
        dob: session.dobMode === "collect" ? dob : null,
        note: note.trim() || null,
        customFields: answers,
      });
      // The customer now exists: no name is asked for again in this session.
      // A date of birth given with this booking is now on file and counts as checked.
      setSession(saveSession(slug, { token: session.token, phone: session.phone, needsName: false, name: session.name ?? (name.trim() || null), dobMode: session.dobMode === "collect" ? "verify" : session.dobMode, dobOk: true }, session.expiresAt - SESSION_MS));
      setDone(visit);
    } catch (err) {
      const code = err instanceof BookingApiError ? err.code : "unexpected_error";
      if (code === "dob_required") {
        setSession(saveSession(slug, { ...session, dobMode: session.dobMode === "off" || !session.dobMode ? "verify" : session.dobMode, dobOk: false }, session.expiresAt - SESSION_MS));
      } else if (code === "invalid_session" || code === "session_limit") {
        endSession(errorMessage(code));
      } else {
        setError(errorMessage(code, err instanceof BookingApiError ? err.detail : null));
        if (code === "slot_unavailable" || code === "time_not_allowed") {
          setPicked(null);
          setRefresh((n) => n + 1);
        }
      }
    } finally {
      setBusy(false);
    }
  }

  function again() {
    setDone(null);
    setServiceIds([]);
    setPicked(null);
    setForSomeone(false);
    setGuestName("");
    setNote("");
    setError(null);
  }

  if (done) {
    return (
      <section className="bk-section bk-narrow" aria-live="polite">
        <div className="bk-card">
          <p className="bk-ok" style={{ margin: 0, fontWeight: 600 }}>Your booking is confirmed.</p>
          <p style={{ margin: "12px 0 0", fontWeight: 600 }}>{done.services.join(" + ")}</p>
          <p style={{ margin: "2px 0 0" }}>
            {formatWeekdayDayMonth(done.starts_at)} at {formatTime(done.starts_at)}
            {done.staff ? ` with ${done.staff}` : ""}
          </p>
          {done.guest_name && <p className="bk-muted" style={{ margin: "2px 0 0" }}>For {done.guest_name}</p>}
          {config.sends_confirmation && session && <p className="bk-muted" style={{ margin: "10px 0 0" }}>A confirmation text is on its way to {formatAuPhoneForDisplay(session.phone)}.</p>}
          <div className="bk-row" style={{ marginTop: 16 }}>
            <Button type="button" variant="primary" onClick={again}>Book something else</Button>
            <Button type="button" variant="ghost" onClick={onManage}>Change or cancel a booking</Button>
          </div>
          {session && <p className="bk-muted" style={{ margin: "12px 0 0" }}>You can book again on this device without another code for the next 20 minutes.</p>}
        </div>
      </section>
    );
  }

  return (
    <div className="bk-layout" onKeyDown={confirmOnEnter}>
      <div className="bk-card bk-area-customer">
        <fieldset className="bk-fieldset" style={{ margin: 0 }}>
          <legend>Customer</legend>
          {!session ? (
            <VerifyStep api={api} purpose="book" siteKey={config.turnstile_site_key} onVerified={onVerified} message={verifyNote} plain hasPhone={!!config.phone} />
          ) : (
            <div className="bk-stack" style={{ gap: 12 }}>
              <p className="bk-muted" style={{ margin: 0 }}>
                {session.name ? `Booking as ${session.name}` : "Mobile checked"} ({formatAuPhoneForDisplay(session.phone)}).{" "}
                <button type="button" className="bk-link" onClick={() => endSession(null)}>Not you?</button>
              </p>
              {session.dobMode === "verify" && !session.dobOk && (
                <DobCheck api={api} token={session.token} onOk={() => setSession(saveSession(slug, { ...session, dobOk: true }, session.expiresAt - SESSION_MS))} onEnded={endSession} />
              )}
              {session.dobMode === "collect" && (
                <label className="bk-label">
                  Your date of birth
                  <Input type="date" value={dob} min="1900-01-01" max={todayInZone(config.timezone)} onChange={(e) => setDob(e.target.value)} autoComplete="bday" />
                </label>
              )}
              {session.needsName && (
                <label className="bk-label">
                  Your name
                  <Input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={80} />
                </label>
              )}
              {mine.length > 0 && (
                <div className="bk-mylist">
                  <strong>Your upcoming bookings</strong>
                  <ul>
                    {mine.map((m) => (
                      <li key={m.id}>
                        {formatWeekdayDayMonth(m.starts_at)} at {formatTime(m.starts_at)}: {m.services.join(" + ")}{m.staff ? ` with ${m.staff}` : ""}{m.guest_name ? ` (for ${m.guest_name})` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <label className="bk-check">
                <input type="checkbox" checked={forSomeone} onChange={(e) => setForSomeone(e.target.checked)} />
                <span>This booking is for someone else</span>
              </label>
              {forSomeone && (
                <label className="bk-label">
                  Their name
                  <Input value={guestName} onChange={(e) => setGuestName(e.target.value)} maxLength={80} />
                </label>
              )}
            </div>
          )}
        </fieldset>
      </div>

      <div className="bk-card bk-area-choose">
        <h2 className="bk-h" style={{ marginBottom: 2 }}>New booking</h2>
        <p className="bk-hint">Pick a service, then a free time on the day view.</p>
        <fieldset className="bk-fieldset">
          <legend>Service(s)</legend>
          <div className="bk-services">
            {groupByCategory(config.services).map((g) => (
              <div key={g.title ?? "_"} className="bk-category">
                {g.title && <h3 className="bk-cat-title">{g.title}</h3>}
                {g.items.map((s) => (
                  <label key={s.id} className="bk-check">
                    <input type="checkbox" checked={serviceIds.includes(s.id)} onChange={() => toggleService(s.id)} />
                    <span className="bk-check-text">
                      <span>{s.name}</span>
                      <span className="bk-muted">{durationLabel(s.duration_minutes)}{s.price_cents != null ? ` · ${formatServicePrice(s.price_cents, s.price_from)}` : ""}</span>
                    </span>
                  </label>
                ))}
              </div>
            ))}
            {config.services.length === 0 && <span className="bk-muted">Nothing can be booked online yet.</span>}
          </div>
          {chosen.length > 1 && <p className="bk-hint" style={{ margin: "6px 0 0" }}>{chosen.length} services, {durationLabel(minutes)} in total, back to back with the same person.</p>}
        </fieldset>
        <div className={`bk-selected${picked ? " bk-selected-on" : ""}`} aria-live="polite">
          {picked
            ? `Selected: ${pickedStaff ?? "staff"}, ${formatWeekdayDayMonth(picked.slot.starts_at)} at ${formatTime(picked.slot.starts_at)}`
            : serviceIds.length > 0
              ? "No time selected yet. Pick an open time on the day view."
              : "Pick a service to see the times that are free."}
        </div>
      </div>

      <div className="bk-area-calendar">
        <SlotCalendar
          api={api}
          config={config}
          serviceIds={serviceIds}
          durationMin={minutes}
          columns={staff}
          selected={picked}
          onSelect={(p) => { setPicked(p); setError(null); }}
          refreshKey={refresh}
          mine={mine}
          browse={!!session}
        />
      </div>

      <div className="bk-card bk-area-you">

        {questions.length > 0 && (
          <fieldset className="bk-fieldset bk-stack" style={{ gap: 10 }}>
            <legend>Details</legend>
            {questions.map((q) => (
              <IntakeFieldInput key={q.key} field={q} value={answers[q.key]} onChange={(v) => setAnswers((a) => ({ ...a, [q.key]: v }))} />
            ))}
          </fieldset>
        )}

        <label className="bk-label" style={{ marginBottom: 12 }}>
          Note (optional)
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={3}
            style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", font: "inherit", fontWeight: 400, resize: "vertical", border: "1px solid var(--rc-border)", borderRadius: 8, background: "var(--rc-surface)", color: "var(--rc-text)" }}
          />
        </label>

        {error && <p className="bk-error" role="alert" style={{ marginTop: 0 }}>{error}</p>}
        <Button type="button" variant="primary" disabled={busy || !ready} onClick={() => void submit()} style={{ width: "100%" }}>{busy ? "Booking..." : "Confirm booking"}</Button>
        {!ready && !busy && (
          <p className="bk-hint" style={{ margin: "8px 0 0" }}>
            {serviceIds.length === 0 ? "Choose a service" : !picked ? "Choose a time" : !session ? "Check your mobile with the texted code" : dobPending ? "Confirm your date of birth" : "Fill in the details above"} to confirm.
          </p>
        )}
      </div>
    </div>
  );
}
